import { useRef, type ReactNode } from 'react';
import { ImagePlus, RotateCw, Scan, Trash2, Blend } from 'lucide-react';
import type { DesignSpec, SurfaceImage } from '@shared/spec';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { TopSurfaceBounds } from './SurfaceImageLayer';
import { useTranslation } from 'react-i18next';

type SurfaceImageControlsProps = {
  spec?: DesignSpec;
  selectedId: string | null;
  topBounds: TopSurfaceBounds | null;
  uploading: boolean;
  onUpload: (file: File) => void;
  onSelect: (id: string | null) => void;
  onChange: (image: SurfaceImage, commit: boolean) => void;
  onRemove: (id: string) => void;
};

export function SurfaceImageControls({
  spec,
  selectedId,
  topBounds,
  uploading,
  onUpload,
  onSelect,
  onChange,
  onRemove,
}: SurfaceImageControlsProps) {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const surfaceImages = spec?.surfaceImages ?? [];
  const selected =
    surfaceImages.find((image) => image.id === selectedId) ?? surfaceImages[0];

  const selectedAsset = selected
    ? spec?.imageAssets?.find((asset) => asset.id === selected.assetId)
    : undefined;
  const aspect =
    selectedAsset?.width && selectedAsset?.height
      ? selectedAsset.height / selectedAsset.width
      : selected
        ? selected.size[1] / selected.size[0]
        : 1;

  const updateSelected = (
    patch: Partial<SurfaceImage>,
    commit: boolean,
  ) => {
    if (!selected) return;
    onChange({ ...selected, ...patch }, commit);
  };

  return (
    <div className="absolute left-3 top-3 z-20 flex max-w-[260px] flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = '';
          if (file) onUpload(file);
        }}
      />

      <div className="inline-flex w-fit items-center gap-1 rounded-lg border-2 border-brand-indigo/15 bg-k-card p-1">
        <IconButton
          label={t('viewer.surfaceImages.add')}
          disabled={!topBounds || uploading}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus className="h-4 w-4" />
        </IconButton>
        {surfaceImages.map((image, index) => (
          <button
            key={image.id}
            type="button"
            onClick={() => onSelect(image.id)}
            className={[
              'h-8 min-w-8 rounded-md px-2 text-xs font-bold transition-colors',
              selected?.id === image.id
                ? 'bg-brand-indigo text-brand-cream'
                : 'text-brand-indigo hover:bg-brand-indigo/10',
            ].join(' ')}
            title={t('viewer.surfaceImages.select', { index: index + 1 })}
          >
            {index + 1}
          </button>
        ))}
      </div>

      {selected && topBounds && (
        <div className="w-[250px] rounded-lg border-2 border-brand-indigo/15 bg-k-card p-3 text-brand-indigo">
          <div className="grid gap-3">
            <ControlRow
              icon={<Scan className="h-4 w-4" />}
              label={t('viewer.surfaceImages.scale')}
              value={selected.size[0]}
              min={Math.max(5, Math.min(topBounds.width, topBounds.depth) * 0.05)}
              max={Math.max(10, Math.min(topBounds.width, topBounds.depth))}
              step={1}
              onValue={(width, commit) =>
                updateSelected(
                  { size: [width, Math.max(1, width * aspect)] },
                  commit,
                )
              }
            />
            <ControlRow
              icon={<RotateCw className="h-4 w-4" />}
              label={t('viewer.surfaceImages.rotate')}
              value={selected.rotationDeg}
              min={-180}
              max={180}
              step={1}
              onValue={(rotationDeg, commit) =>
                updateSelected({ rotationDeg }, commit)
              }
            />
            <ControlRow
              icon={<Blend className="h-4 w-4" />}
              label={t('viewer.surfaceImages.opacity')}
              value={selected.opacity}
              min={0.1}
              max={1}
              step={0.05}
              onValue={(opacity, commit) => updateSelected({ opacity }, commit)}
            />
          </div>

          <div className="mt-3 flex justify-end">
            <IconButton
              label={t('viewer.surfaceImages.remove')}
              onClick={() => onRemove(selected.id)}
            >
              <Trash2 className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      )}
    </div>
  );
}

function ControlRow({
  icon,
  label,
  value,
  min,
  max,
  step,
  onValue,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onValue: (value: number, commit: boolean) => void;
}) {
  return (
    <div className="grid grid-cols-[20px_1fr_42px] items-center gap-2">
      <span className="text-brand-indigo/80">{icon}</span>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([next]) => onValue(next, false)}
        onValueCommit={([next]) => onValue(next, true)}
      />
      <span className="text-right text-[10px] font-bold tabular-nums text-brand-indigo/70" title={label}>
        {Number.isInteger(value) ? value : value.toFixed(2)}
      </span>
    </div>
  );
}

function IconButton({
  label,
  children,
  disabled,
  onClick,
}: {
  label: string;
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          onClick={onClick}
          className="h-8 w-8"
          aria-label={label}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
